import type { ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { Image } from '../Image/Image';

const StyledFeatureCardImageContainer = styled.div`
  --wui-feature-card-image-border-radius: var(--wui-border-radius-02);
  --wui-feature-card-image-height: auto;
  --wui-feature-card-image-width: auto;

  overflow: hidden;
  padding: var(--wui-space-04);
  height: var(--wui-feature-card-image-height);
  width: var(--wui-feature-card-image-width);
  justify-self: stretch;
  align-self: stretch;
  max-width: 300px;

  /* Make image container stretch full width in vertical layout and remove bottom padding */
  [data-wui-feature-card-orientation='vertical'] & {
    max-width: none;
    width: 100%;
    padding-bottom: var(--wui-space-00);
  }

  /* In horizontal layout, make image height equal to the content height and apply aspect ratio */
  [data-wui-feature-card-orientation='horizontal'] & {
    --wui-feature-card-image-height: calc(
      var(--wui-feature-card-content-height) + (var(--wui-feature-card-padding) * 2)
    );

    /* Handle image alignment and remove padding next to content */
    &[data-wui-feature-card-image='right'] {
      padding-left: var(--wui-space-00);
      order: 2;
    }

    &[data-wui-feature-card-image='left'] {
      padding-right: var(--wui-space-00);
    }
  }

  /* Remove border radius and padding in small prominence horizontal layout */
  [data-wui-feature-card-content-prominence='small'] & {
    --wui-feature-card-image-border-radius: var(--wui-border-radius-00);

    padding: var(--wui-space-00);
  }

  /* Remove border radius in secondary prominence vertical layout */
  [data-wui-feature-card-orientation='vertical']
    [data-wui-feature-card-content-prominence='secondary']
    & {
    --wui-feature-card-image-border-radius: var(--wui-border-radius-00);
  }

  [data-wui-feature-card-orientation='horizontal'][data-wui-feature-card-content-prominence='small']
    & {
    flex: 0 0 25%;
  }

  img {
    border-radius: var(--wui-feature-card-image-border-radius);
  }
`;

export type FeatureCardImageProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The URL of the image to display in the feature card
   */
  src: string;
  /**
   * Alternative text description of the image for accessibility
   */
  alt?: string;
  /**
   * Position of the image within the feature card layout. Only applies in horizontal orientation.
   */
  alignment?: 'left' | 'right';
};

export const FeatureCardImage = ({
  alignment = 'left',
  alt,
  src,
  ...props
}: FeatureCardImageProps) => {
  return (
    <StyledFeatureCardImageContainer
      data-wui-feature-card-image={alignment}
      {...props}
    >
      <Image
        alt={alt ?? ''}
        fill={true}
        fit="cover"
        src={src}
      />
    </StyledFeatureCardImageContainer>
  );
};
